import {
  FeatureName,
  OrderByInput,
  useIsFeatureEnabled,
  useRoutineListOrder,
  useSetRoutineListOrder,
} from '@gripp/shared-logic';
import { InfiniteDataGrid } from '@gripp/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import { StyleSheet, View } from 'react-native';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { useColumnsRoutine } from './useColumnsRoutine';

type RoutineListProps = {
  filter: any;
  paddingBottom?: number;
  updateRowsSelected?: (rows: Array<any>) => void;
  showCheckboxColumn: boolean;
  showImpersonateWorkspace: boolean;
  onItemsCountChange?: (count: number) => void;
  headerContentComponent?: React.ReactNode;
  assetsButton: (data: any) => React.ReactNode;
  addonsButton?: (data: any) => React.ReactNode;
  onRowClick?: (row: any) => void;
  isProducerWorkspace?: boolean;
};

export const RoutineList = ({
  filter,
  paddingBottom = 10,
  updateRowsSelected,
  showCheckboxColumn,
  headerContentComponent,
  assetsButton,
  addonsButton,
  onRowClick,
  isProducerWorkspace = false,
}: RoutineListProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);
  const scrollViewOffset = isCropCircleRedesignEnabled ? 100 : 70;
  const windowOffset = isCropCircleRedesignEnabled ? 0 : 150;
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(scrollViewOffset, 0, windowOffset);

  const { data: routineOrder } = useRoutineListOrder();
  const { mutateAsync: setRoutineOrder } = useSetRoutineListOrder();

  const columns = useColumnsRoutine(
    showCheckboxColumn,
    assetsButton,
    isProducerWorkspace,
    addonsButton
  ) as ColumnDef<any, any>[];

  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setRoutineOrder(orderBy);
  };

  return (
    <View
      style={[
        styles.routinesContainer,
        {
          maxHeight: containerMaxHeight,
        },
      ]}
    >
      <InfiniteDataGrid
        columns={columns}
        paddingBottom={paddingBottom}
        query={LIST_ROUTINES}
        dataPrefix="routine.listRoutines"
        modelName="routine"
        orderBy={routineOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        listMaxHeight={listMaxHeight}
        showScrollIndicator={true}
        updateRowsSelected={updateRowsSelected}
        onOrderByChange={onOrderByChange}
        headerContent={headerContentComponent}
        onRowClick={onRowClick}
      />
    </View>
  );
};

const LIST_ROUTINES = `
  query FindRoutines($filter: ListRoutinesFilter, $page: PageRequest, $orderBy: OrderByInput) {
    routine {
      listRoutines(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          name
          type
          config
          assetsCount
          allAssetsCount
          addonsCount
          submissionsCount
          groups {
            id
            name
          }
          lastSubmitted
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  routinesContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
});